import {
  Home,
  Cable,
  PowerOff,
  Radar,
  ShieldCheck,
  LayoutDashboard,
  HardDrive,
  Fingerprint,
  ShieldAlert,
  FolderKanban,
  Briefcase,
  Route,
  FileBarChart2,
  Settings,
  Server,
  Waypoints,
  Share2,
  ScanSearch,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';
import type { FeatureKey } from '@/lib/entitlements';

/**
 * Sidebar navigation model — mirrors the RxBB Figma "Home" frame
 * (file tXMriYKFQT546L05gBJRuj, node 11050:1520). Two shapes: a flat `item`
 * (single link) and a collapsible `group` (the "Orbis" product modules, each
 * expanding to a panel of sub-pages). Icons map to lucide equivalents of the
 * Figma "Square Icons" set.
 *
 * `feature` ties an entry to an Azure Marketplace plan feature key
 * (GET /sso-api/marketplace/access → enabledFeatures). Untagged entries are
 * always shown; tagged ones are hidden when the plan lacks the key. A group's
 * key applies to all of its children.
 */
export interface NavLeaf {
  label: string;
  to: string;
  icon?: LucideIcon;
  feature?: FeatureKey;
}

export interface NavItem extends NavLeaf {
  type: 'item';
  icon: LucideIcon;
}

export interface NavGroup {
  type: 'group';
  label: string;
  icon: LucideIcon;
  feature?: FeatureKey;
  children: NavLeaf[];
}

export type NavEntry = NavItem | NavGroup;

export const navEntries: NavEntry[] = [
  { type: 'item', label: 'Home', to: '/home', icon: Home },
  { type: 'item', label: 'Connectors', to: '/connectors', icon: Cable },
  {
    type: 'group',
    label: 'Orbis Decommission',
    icon: PowerOff,
    children: [
      { label: 'Dashboard', to: '/decommission', icon: LayoutDashboard },
      { label: 'Partners & APIs', to: '/decommission/partners', icon: HardDrive },
      { label: 'Configurations', to: '/decommission/configurations', icon: Fingerprint },
      { label: 'Reports', to: '/decommission/reports', icon: FileBarChart2 },
    ],
  },
  {
    type: 'group',
    label: 'Asset Management',
    icon: Server,
    feature: 'inventory',
    children: [
      { label: 'Devices', to: '/asset-management/devices', icon: HardDrive },
      { label: 'Identities', to: '/asset-management/identities', icon: Fingerprint },
    ],
  },
  {
    type: 'group',
    label: 'Decommission Intelligence',
    icon: Waypoints,
    feature: 'scoring',
    children: [
      { label: 'Dependency Map', to: '/orbis-intelligence/graph', icon: Share2 },
      { label: 'Insight Scan', to: '/orbis-intelligence/insight-scan', icon: ScanSearch },
      { label: 'Orbis AI Search', to: '/orbis-intelligence/ai-search', icon: Sparkles },
    ],
  },
  {
    type: 'group',
    label: 'Threat Intelligence',
    icon: Radar,
    children: [
      { label: 'Dashboard', to: '/intel', icon: LayoutDashboard },
      { label: 'Threat Analysis', to: '/intel/threats', icon: ShieldAlert },
      { label: 'Projects', to: '/intel/projects', icon: FolderKanban },
    ],
  },
  {
    type: 'group',
    label: 'Orbis AI SOC',
    icon: ShieldCheck,
    children: [
      { label: 'Dashboard', to: '/soc', icon: LayoutDashboard },
      { label: 'Case Management', to: '/soc/cases', icon: Briefcase },
      { label: 'Trails', to: '/soc/trails', icon: Route },
      { label: 'Reports', to: '/soc/reports', icon: FileBarChart2 },
    ],
  },
  { type: 'item', label: 'Reports', to: '/reports', icon: FileBarChart2 },
  { type: 'item', label: 'Settings', to: '/settings', icon: Settings },
];

/** Flat list of every navigable path — used to register placeholder routes. */
export const navPaths: string[] = navEntries.flatMap((entry) =>
  entry.type === 'item' ? [entry.to] : entry.children.map((c) => c.to),
);

/** Plan feature key gating a nav path (a leaf's own key, else its group's). */
export function navFeatureFor(path: string): FeatureKey | undefined {
  for (const entry of navEntries) {
    if (entry.type === 'item') {
      if (entry.to === path) return entry.feature;
      continue;
    }
    const child = entry.children.find((c) => c.to === path);
    if (child) return child.feature ?? entry.feature;
  }
  return undefined;
}

/**
 * Nav tree with entries the current plan doesn't include removed; groups left
 * with no visible children are dropped.
 */
export function filterNavByFeature(
  entries: NavEntry[],
  isEnabled: (feature?: FeatureKey) => boolean,
): NavEntry[] {
  return entries.flatMap<NavEntry>((entry) => {
    if (!isEnabled(entry.feature)) return [];
    if (entry.type === 'item') return [entry];
    const children = entry.children.filter((c) => isEnabled(c.feature));
    return children.length ? [{ ...entry, children }] : [];
  });
}
